import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useSettings } from "./useSettings";

/**
 * Système Univers multi-marques (cockpit foundation — issu de Jardins d'Éole).
 *
 * Si universes_enabled=false → fallback transparent vers settings legacy (rétro-compat 100%).
 * Si universes_enabled=true → le hook useUniverse() retourne :
 *   - active: { key, name, label, icon, primary_color, accent_color, phone, hero_*, presentation_*, ... }
 *   - activeKey, setActiveKey
 *   - all: array des univers configurés
 *   - enabled: bool
 *
 * Persistance : localStorage `cockpit_active_universe` (par site).
 */
const UniverseContext = createContext({
  enabled: false,
  active: null,
  activeKey: null,
  all: [],
  setActiveKey: () => {},
});

const LS_KEY = "cockpit_active_universe";

export function UniverseProvider({ children }) {
  const { settings: s } = useSettings();
  const enabled = !!s?.universes_enabled;
  const data = s?.universes_data || {};

  const all = useMemo(() => {
    return Object.entries(data)
      .filter(([key, u]) => key && u && (u.name || u.label))
      .map(([key, u]) => ({ key, ...u }))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [data]);

  // Initial active key : localStorage > default settings > première clé dispo
  const defaultKey = s?.universes_active_default || (all[0]?.key || "");
  const [activeKey, setActiveKey] = useState(() => {
    if (typeof window === "undefined") return defaultKey;
    return localStorage.getItem(LS_KEY) || defaultKey;
  });

  // Persiste à chaque changement
  useEffect(() => {
    if (!enabled) return;
    if (activeKey && typeof window !== "undefined") {
      localStorage.setItem(LS_KEY, activeKey);
    }
  }, [activeKey, enabled]);

  // Si la clé courante n'existe plus dans les data (univers supprimé), revient au défaut
  useEffect(() => {
    if (!enabled) return;
    if (activeKey && all.length > 0 && !all.find(u => u.key === activeKey)) {
      setActiveKey(all[0].key);
    }
  }, [activeKey, all, enabled]);

  const switchTo = useCallback((key) => setActiveKey(key), []);

  const active = useMemo(() => {
    if (!enabled || !activeKey) return null;
    return all.find(u => u.key === activeKey) || null;
  }, [enabled, activeKey, all]);

  const value = useMemo(() => ({
    enabled, active, activeKey, all, setActiveKey: switchTo,
  }), [enabled, active, activeKey, all, switchTo]);

  return <UniverseContext.Provider value={value}>{children}</UniverseContext.Provider>;
}

/**
 * Hook principal — usage : const { active, all, setActiveKey, enabled } = useUniverse();
 */
export function useUniverse() {
  return useContext(UniverseContext);
}

/**
 * Hook utilitaire : retourne le téléphone de l'univers actif, sinon le téléphone global Settings.
 * Tous les composants qui rendent le téléphone DOIVENT utiliser ce hook plutôt que settings.phone.
 */
export function useUniversePhone() {
  const { settings: s } = useSettings();
  const { active, enabled } = useUniverse();
  if (enabled && active?.phone) return active.phone;
  return s?.phone || "";
}

/**
 * Helper : merge les valeurs de l'univers actif avec les settings globaux.
 * Pour un champ donné (ex: primary_color), retourne d'abord la valeur de l'univers, sinon le global.
 * Permet à un composant de fonctionner en mode legacy ET multi-univers sans changer son code.
 */
export function useUniverseField(field, fallback = "") {
  const { settings: s } = useSettings();
  const { active, enabled } = useUniverse();
  if (enabled && active && active[field] != null && active[field] !== "") return active[field];
  return s?.[field] ?? fallback;
}
